import { Box, Grid, Stack, Tooltip, Typography } from '@mui/material'
import { FC } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { tokens } from '@/theme'
import { ETAPAS_COMERCIAIS, ETAPA_COMERCIAL_LABEL, EtapaComercial } from '@/types/statusReserva'
import {
  CANAL_RECORTE_COR,
  CANAL_RECORTE_LABEL,
  CanalDeRecorte,
  formatBRL,
  formatBRLCurto,
  formatPct,
} from './canais'
import { LegendaCanais } from './LegendaCanais'

export interface SerieCanal {
  canal: CanalDeRecorte
  /** Contagem por `EtapaComercial` — o `pipeline.porEtapaComercial` do recorte. */
  porEtapa: Record<string, number>
  /** R$ por `EtapaComercial` — o `pipeline.valorPorEtapaComercial` do recorte (opcional). */
  valorPorEtapa?: Record<string, number>
}

const ALTURA_GRAFICO = 96
const BRANCO = '#FFFFFF'
const BRANCO_APOIO = 'rgba(255,255,255,0.72)'
const BRANCO_LINHA = 'rgba(255,255,255,0.24)'

/**
 * Identidade visual por etapa: o funil avança numa rampa do laranja Originei
 * (tint claro na Reserva → laranja cheio na Negociação), "Ganho" é o card de
 * destaque em Ink com o número em laranja (o mesmo par do primeiro KPI da
 * página) e "Perdido" fica apagado. Só apresentação.
 */
interface EstiloEtapa {
  faixa: string
  rotulo: string
  fundo: string
  texto: string
  apoio: string
  escuro?: boolean
}
const CLARO: Pick<EstiloEtapa, 'rotulo' | 'fundo' | 'texto' | 'apoio'> = {
  rotulo: tokens.brandPrimaryDark,
  fundo: tokens.surfaceCard,
  texto: tokens.brandInk,
  apoio: tokens.textSecondary,
}
const ESTILO_ETAPA: Record<EtapaComercial, EstiloEtapa> = {
  RESERVA: { ...CLARO, faixa: '#F9B79A' },
  REUNIAO: { ...CLARO, faixa: tokens.brandPrimaryLight },
  ESTRUTURACAO: { ...CLARO, faixa: tokens.brandPrimary },
  NEGOCIACAO: { ...CLARO, faixa: tokens.brandPrimaryDark },
  GANHO: {
    faixa: tokens.brandPrimary,
    rotulo: tokens.brandPrimaryLight,
    fundo: tokens.brandInk,
    texto: BRANCO,
    apoio: BRANCO_APOIO,
    escuro: true,
  },
  PERDIDO: {
    faixa: '#C4C9D1',
    rotulo: tokens.textSecondary,
    fundo: tokens.surfacePage,
    texto: tokens.textSecondary,
    apoio: tokens.textSecondary,
  },
}

/**
 * Funil por canal da Visão de Carteira: um card por etapa comercial, com o total
 * da etapa em destaque (reservas e R$), a participação no funil e um gráfico de
 * colunas verticais — uma coluna por canal — na mesma escala em todos os cards
 * (a coluna mais alta da tela é o maior número do funil). Os números vêm DIRETO
 * do `/v1/dashboard/resumo?canal=` de cada canal; aqui só se soma e se desenha.
 * MUI/Box puro, sem lib de gráfico (regra da Onda 2).
 */
export const FunilPorCanal: FC<{
  series: SerieCanal[]
  /**
   * Rota aberta ao clicar no card de cada etapa. Etapa com rota vira link (com
   * destaque no hover/foco); sem rota, o card fica estático como antes.
   */
  rotaPorEtapa?: Partial<Record<EtapaComercial, string>>
}> = ({ series, rotaPorEtapa }) => {
  const maximo = Math.max(
    1,
    ...series.flatMap((s) => ETAPAS_COMERCIAIS.map((e) => s.porEtapa[e] ?? 0)),
  )
  const totalPorEtapa = (etapa: EtapaComercial) =>
    series.reduce((acc, s) => acc + (s.porEtapa[etapa] ?? 0), 0)
  const valorPorEtapa = (etapa: EtapaComercial) =>
    series.reduce((acc, s) => acc + Number(s.valorPorEtapa?.[etapa] ?? 0), 0)
  const totalFunil = ETAPAS_COMERCIAIS.reduce((acc, e) => acc + totalPorEtapa(e), 0)

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <LegendaCanais canais={series.map((s) => s.canal)} />
      </Box>

      <Grid container spacing={1.5}>
        {ETAPAS_COMERCIAIS.map((etapa) => {
          const total = totalPorEtapa(etapa)
          const valor = valorPorEtapa(etapa)
          const estilo = ESTILO_ETAPA[etapa]
          const borda = estilo.escuro ? tokens.brandInk : tokens.divider
          const linhaBase = estilo.escuro ? BRANCO_LINHA : tokens.divider
          const corPct = estilo.escuro ? BRANCO : estilo.rotulo
          const rota = rotaPorEtapa?.[etapa]
          const link = rota
            ? {
                component: RouterLink,
                to: rota,
                'aria-label': `Abrir ${ETAPA_COMERCIAL_LABEL[etapa]}`,
              }
            : {}
          return (
            <Grid item xs={6} sm={4} md={2} key={etapa}>
              <Box
                data-testid="etapa-card"
                data-etapa={etapa}
                {...link}
                sx={{
                  ...(rota && {
                    textDecoration: 'none',
                    cursor: 'pointer',
                    outline: 'none',
                    position: 'relative',
                    transition: 'transform 180ms ease, box-shadow 180ms ease',
                    '&:hover': {
                      transform: 'translateY(-4px) scale(1.03)',
                      boxShadow: estilo.escuro
                        ? '0 12px 24px rgba(0,0,0,0.28)'
                        : '0 12px 24px rgba(16,24,40,0.12)',
                      zIndex: 1,
                    },
                    '&:focus-visible': {
                      transform: 'translateY(-4px) scale(1.03)',
                      boxShadow: `0 0 0 3px ${tokens.brandPrimaryLight}`,
                      zIndex: 1,
                    },
                    '@media (prefers-reduced-motion: reduce)': {
                      transition: 'none',
                      '&:hover, &:focus-visible': { transform: 'none' },
                    },
                  }),
                  height: '100%',
                  border: `1px solid ${borda}`,
                  borderTop: `4px solid ${estilo.faixa}`,
                  borderRadius: 2,
                  backgroundColor: estilo.fundo,
                  p: 1.75,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                <Typography
                  variant="caption"
                  component="div"
                  sx={{
                    textTransform: 'uppercase',
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    color: estilo.rotulo,
                  }}
                >
                  {ETAPA_COMERCIAL_LABEL[etapa]}
                </Typography>

                <Box>
                  <Typography
                    component="div"
                    sx={{
                      fontSize: '1.875rem',
                      fontWeight: 700,
                      lineHeight: 1.1,
                      letterSpacing: '-0.02em',
                      color: estilo.escuro ? tokens.brandPrimary : estilo.texto,
                    }}
                  >
                    {total}
                  </Typography>
                  <Typography variant="caption" sx={{ color: estilo.apoio }}>
                    {total === 1 ? 'reserva' : 'reservas'} ·{' '}
                    <Box component="span" sx={{ fontWeight: 700, color: corPct }}>
                      {formatPct(total, totalFunil)}
                    </Box>{' '}
                    do funil
                  </Typography>
                </Box>

                <Tooltip title={formatBRL(valor)} placement="top" arrow>
                  <Typography
                    component="div"
                    sx={{ fontWeight: 600, fontSize: '0.875rem', color: estilo.texto }}
                  >
                    {formatBRLCurto(valor)}
                  </Typography>
                </Tooltip>

                {/* Colunas verticais: uma por canal, escala global do funil. */}
                <Stack
                  direction="row"
                  spacing={0.75}
                  alignItems="flex-end"
                  sx={{
                    mt: 'auto',
                    height: ALTURA_GRAFICO + 18,
                    borderBottom: `1px solid ${linhaBase}`,
                  }}
                >
                  {series.map((s) => {
                    const n = s.porEtapa[etapa] ?? 0
                    const altura = n > 0 ? Math.max((n / maximo) * ALTURA_GRAFICO, 6) : 3
                    return (
                      <Box
                        key={s.canal}
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          gap: 0.25,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: '0.6875rem',
                            fontWeight: 600,
                            lineHeight: 1,
                            color: n > 0 ? estilo.texto : estilo.apoio,
                            opacity: n > 0 ? 1 : 0.7,
                          }}
                        >
                          {n}
                        </Typography>
                        <Box
                          role="img"
                          aria-label={`${ETAPA_COMERCIAL_LABEL[etapa]} — ${CANAL_RECORTE_LABEL[s.canal]}: ${n}`}
                          sx={{
                            width: '100%',
                            maxWidth: 22,
                            height: altura,
                            borderRadius: '3px 3px 0 0',
                            backgroundColor: CANAL_RECORTE_COR[s.canal],
                            opacity: n > 0 ? 1 : 0.3,
                            transition: 'height 200ms',
                          }}
                        />
                      </Box>
                    )
                  })}
                </Stack>
              </Box>
            </Grid>
          )
        })}
      </Grid>
    </Box>
  )
}

export default FunilPorCanal
